
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User } from 'firebase/auth';
import { 
  loginWithEmail, 
  signupWithEmail, 
  logoutUser, 
  getCurrentUser, 
  onUserStateChanged,
  resetPasswordEmail,
  setupPhoneAuth,
  verifyPhoneAuthCode,
  getUserProfile,
  updateUserProfile
} from '@/services/firebase';
import { toast } from '@/utils/toast';

interface UserProfile {
  displayName?: string;
  email?: string;
  phoneNumber?: string;
  photoURL?: string;
  preferredLanguage?: string;
  createdAt?: string;
  lastActive?: string;
  auraPoints?: number;
  [key: string]: any;
}

interface AuthContextProps {
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  sendPhoneVerification: (phoneNumber: string) => Promise<string>;
  verifyPhoneCode: (verificationId: string, code: string) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  updateProfile: (profileData: Partial<UserProfile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile data whenever the current user changes
  const fetchUserProfile = useCallback(async (user: User) => {
    try {
      const profile = await getUserProfile(user.uid);
      setUserProfile(profile);
      
      // Update lastActive timestamp
      if (profile) {
        await updateUserProfile(user.uid, {
          lastActive: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error("Error loading user profile:", error);
    }
  }, []);

  // Refresh user profile data on demand
  const refreshUserProfile = useCallback(async () => {
    if (currentUser) {
      await fetchUserProfile(currentUser);
    }
  }, [currentUser, fetchUserProfile]);

  // Update user profile
  const updateProfile = useCallback(async (profileData: Partial<UserProfile>) => {
    if (!currentUser) {
      throw new Error('No user logged in');
    }
    
    try {
      await updateUserProfile(currentUser.uid, profileData);
      await refreshUserProfile();
    } catch (error) {
      console.error('Failed to update profile:', error);
      throw error;
    }
  }, [currentUser, refreshUserProfile]);

  useEffect(() => {
    console.log("AuthProvider initializing...");
    // Check for existing user immediately
    const user = getCurrentUser();
    if (user) {
      console.log("User already logged in:", user.email);
      setCurrentUser(user);
      fetchUserProfile(user)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }

    // Set up auth state listener
    const unsubscribe = onUserStateChanged((user) => {
      console.log("Auth state changed:", user?.email || "No user");
      setCurrentUser(user);
      
      if (user) {
        fetchUserProfile(user);
      } else {
        setUserProfile(null);
      }
    });

    return unsubscribe;
  }, [fetchUserProfile]);

  const login = async (email: string, password: string) => {
    try {
      await loginWithEmail(email, password);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to log in';
      toast.error(errorMessage);
      throw error;
    }
  };

  const signup = async (email: string, password: string, displayName?: string) => {
    try {
      await signupWithEmail(email, password, displayName);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create account';
      toast.error(errorMessage);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await logoutUser();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to log out';
      toast.error(errorMessage);
      throw error;
    }
  };
  
  const resetPassword = async (email: string) => {
    try {
      await resetPasswordEmail(email);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send reset email';
      toast.error(errorMessage);
      throw error;
    }
  };
  
  const sendPhoneVerification = async (phoneNumber: string) => {
    try {
      const verificationId = await setupPhoneAuth(phoneNumber);
      return verificationId;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send verification code';
      toast.error(errorMessage);
      throw error;
    }
  };
  
  const verifyPhoneCode = async (verificationId: string, code: string) => {
    try {
      await verifyPhoneAuthCode(verificationId, code);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to verify code';
      toast.error(errorMessage);
      throw error;
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    login,
    signup,
    logout,
    resetPassword,
    sendPhoneVerification,
    verifyPhoneCode,
    refreshUserProfile,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading ? children : (
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
